import { useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { txt } from "@/lib/format";
import { toast } from "sonner";
import { Ban, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { api, errorText } from "@/lib/api";
import { CATEGORY_ICONS } from "@/lib/icons";
import { BilingualFields } from "./Bilingual";
import { ImageUpload } from "./ImageUpload";
import { LangSwitch } from "@/components/LangSwitch";
import { useAdminLang } from "./context";

const toForm = (c, defaultMenu) => ({
  menu_id: c?.menu_id || defaultMenu || null,
  name_en: c?.name_en || "", name_ar: c?.name_ar || "", description_en: c?.description_en || "", description_ar: c?.description_ar || "",
  image_url: c?.image_url || null, icon: c?.icon || null, is_visible: c?.is_visible ?? true,
});

export default function CategoryEditor({ category, onClose, onSaved, t, menus = [] }) {
  const [form, setForm] = useState(() => toForm(category, menus[0]?.id));
  const [err, setErr] = useState(false);
  const [saving, setSaving] = useState(false);
  const { lang, setLang } = useAdminLang();
  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (k.startsWith("name_") && v.trim()) setErr(false);
  };

  const save = async () => {
    if (!form.name_en.trim() && !form.name_ar.trim()) {
      setErr(true);
      return toast.error(t("val.name"));
    }
    setSaving(true);
    try {
      const r = category?.id ? await api.put(`/admin/categories/${category.id}`, form) : await api.post("/admin/categories", form);
      toast.success(t("toast.saved"));
      onSaved(r.data);
    } catch (e) {
      toast.error(errorText(e, t("toast.saveFailed")));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto" data-testid="category-editor">
        <DialogHeader className="flex-row flex-wrap items-center justify-between gap-3 space-y-0 pe-8">
          <DialogTitle>{category?.id ? t("cat.editTitle") : t("cat.newTitle")}</DialogTitle>
          <LangSwitch lang={lang} onChange={setLang} testid="category-editor-lang-switch" />
        </DialogHeader>
        {err && <p className="text-xs text-red-400" data-testid="category-name-error">{t("val.name")}</p>}
        <BilingualFields form={form} set={set} t={t} testPrefix="category" errors={err ? { name: true } : {}} fields={[{ key: "name" }, { key: "description", multiline: true }]} />
        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <Label>{t("cat.menu")}</Label>
            <Select value={form.menu_id || ""} onValueChange={(v) => set("menu_id", v)}>
              <SelectTrigger data-testid="category-menu-select"><SelectValue placeholder={t("mn.unassigned")} /></SelectTrigger>
              <SelectContent>{menus.map((m) => <SelectItem key={m.id} value={m.id} data-testid={`category-menu-option-${m.slug}`}>{txt(m, "name", lang)}</SelectItem>)}</SelectContent>
            </Select>
            <Label className="block pt-2">{t("cat.image")}</Label>
            <ImageUpload value={form.image_url} onChange={(v) => set("image_url", v)} t={t} testid="category-image-upload" aspect="16/9" />
          </div>
          <div className="space-y-3">
            <Label>{t("cat.icon")}</Label>
            <div className="grid grid-cols-6 gap-2">
              <button type="button" onClick={() => set("icon", null)} aria-pressed={!form.icon} title={t("cat.noIcon")} data-testid="category-icon-none"
                className={`grid aspect-square place-items-center rounded-lg border ${!form.icon ? "border-primary bg-primary/15 text-primary" : "text-muted-foreground"}`}><Ban className="h-4 w-4" /></button>
              {Object.entries(CATEGORY_ICONS).map(([name, Icon]) => (
                <button key={name} type="button" onClick={() => set("icon", name)} aria-pressed={form.icon === name} title={name} data-testid={`category-icon-${name}`}
                  className={`grid aspect-square place-items-center rounded-lg border transition-colors ${form.icon === name ? "border-primary bg-primary/15 text-primary" : "text-muted-foreground hover:text-foreground"}`}><Icon className="h-4 w-4" /></button>
              ))}
            </div>
            <label className="flex items-center gap-2 pt-2 text-sm"><Switch checked={form.is_visible} onCheckedChange={(v) => set("is_visible", v)} data-testid="category-visible-switch" />{t("cat.visible")}</label>
            <p className="text-xs text-muted-foreground">{t("cat.hideTip")}</p>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="category-cancel-btn">{t("common.cancel")}</Button>
          <Button onClick={save} disabled={saving} data-testid="category-save-btn">{saving && <Loader2 className="h-4 w-4 animate-spin" />}{saving ? t("common.saving") : t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
